import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { ActionStatus } from '@/application/types';
import { formatMap, languageMap, mediaServerUrl500, mediumMap } from '@/helpers/data';
import { Medium } from '@/domain/types';
import ImageWithFallback from '../image-with-fallback/image-with-fallback.vue';
import ShowImageDialog from '../show-image-dialog/show-image-dialog.vue';
import { FallbackImage, FallbackImageToken, ProductIdentification, ProductMasterData } from '../../types';

const productSearchState = namespace('productSearch');

@Component({
  components: {
    ImageWithFallback,
    ShowImageDialog,
  }
})
export default class MasterDataTab extends Vue {

  @productSearchState.State
  readonly getProductIdentificationsByEANStatus!: ActionStatus;

  @productSearchState.State
  readonly getProductIdentificationStatus!: ActionStatus;

  @productSearchState.State
  readonly getProductMasterDataStatus!: ActionStatus;

  @productSearchState.State
  readonly currentProductIdentification!: ProductIdentification | null;

  @productSearchState.State
  readonly currentProductMasterData!: ProductMasterData | null;

  readonly languageMap = languageMap;
  readonly formatMap = formatMap;
  readonly mediaServerUrl500 = mediaServerUrl500;

  get isLoadingProductIdentification(): boolean {
    return this.getProductIdentificationStatus === ActionStatus.InProgress
      || this.getProductIdentificationsByEANStatus === ActionStatus.InProgress;
  }

  get isLoadingMasterData(): boolean {
    return this.isLoadingProductIdentification
      || this.getProductMasterDataStatus === ActionStatus.InProgress;
  }

  get isProductCalendar(): boolean {
    return this.currentProductIdentification?.medium === 'KAL';
  }

  get calendarImages(): FallbackImage[] {
    const tokens: FallbackImageToken[] = [
      { suffix: '0', fallbackTitle: 'Cover' },
      { suffix: '1', fallbackTitle: 'Januar' },
      { suffix: '2', fallbackTitle: 'Februar' },
      { suffix: '3', fallbackTitle: 'März' },
      { suffix: '4', fallbackTitle: 'April' },
      { suffix: '5', fallbackTitle: 'Mai' },
      { suffix: '6', fallbackTitle: 'Juni' },
      { suffix: '7', fallbackTitle: 'Juli' },
      { suffix: '8', fallbackTitle: 'August' },
      { suffix: '9', fallbackTitle: 'September' },
      { suffix: '10', fallbackTitle: 'Oktober' },
      { suffix: '11', fallbackTitle: 'November' },
      { suffix: '12', fallbackTitle: 'Dezember' },
      { suffix: '13', fallbackTitle: 'Übersicht' },
    ];

    return tokens
      .map((token) => ({
        src: this.calendarImageForIndex(token.suffix),
        fallbackTitle: token.fallbackTitle
      }));
  }

  get isProductCanvas(): boolean {
    return this.currentProductIdentification?.medium === 'CANVAS';
  }

  get canvasImages(): FallbackImage[] {
    const tokens: FallbackImageToken[] = [
      { suffix: '__s_11', fallbackTitle: 'Leinwand' },
      { suffix: '__s_12', fallbackTitle: 'Schräg' },
      { suffix: '__s_13', fallbackTitle: 'Vorne / Hinten' },
      { suffix: '__s_1', fallbackTitle: 'Im Zimmer klein' },
      { suffix: '__s_2', fallbackTitle: 'Im Zimmer groß' },
      { suffix: '__s_3', fallbackTitle: 'Fallback' },
      { suffix: '__0', fallbackTitle: 'Fallback' },
    ];

    return tokens
      .map((token) => ({
        src: this.canvasImageForSuffix(token.suffix),
        fallbackTitle: token.fallbackTitle
      }));
  }

  get isProductPuzzle(): boolean {
    return this.currentProductIdentification?.medium === 'PUZZLE';
  }

  get puzzleImages(): FallbackImage[] {
    const tokens: FallbackImageToken[] = [
      { suffix: '__karton_3d', fallbackTitle: 'Karton' },
      { suffix: '_0', fallbackTitle: 'Motiv' },
      { suffix: '__teile', fallbackTitle: 'Puzzelteile' },
    ];

    return tokens
      .map((token) => ({
        src: this.puzzleImageForIndex(token.suffix),
        fallbackTitle: token.fallbackTitle
      }));
  }

  get isProductPlanner(): boolean {
    return this.currentProductIdentification?.medium === 'PLANNER';
  }

  get plannerImages(): FallbackImage[] {
    const tokens: FallbackImageToken[] = [
      { suffix: '0', fallbackTitle: 'Cover' },
      { suffix: '1', fallbackTitle: 'Januar' },
      { suffix: '2', fallbackTitle: 'Februar' },
      { suffix: '3', fallbackTitle: 'März' },
      { suffix: '4', fallbackTitle: 'April' },
      { suffix: '5', fallbackTitle: 'Mai' },
      { suffix: '6', fallbackTitle: 'Juni' },
      { suffix: '7', fallbackTitle: 'Juli' },
      { suffix: '8', fallbackTitle: 'August' },
      { suffix: '9', fallbackTitle: 'September' },
      { suffix: '10', fallbackTitle: 'Oktober' },
      { suffix: '11', fallbackTitle: 'November' },
      { suffix: '12', fallbackTitle: 'Dezember' },
      { suffix: '13', fallbackTitle: 'Übersicht' },
    ];

    return tokens
      .map((token) => ({
        src: this.plannerImageForIndex(token.suffix),
        fallbackTitle: token.fallbackTitle
      }));
  }

  get isProductBook(): boolean {
    return this.currentProductIdentification?.medium === 'BOOK';
  }

  get bookImages(): FallbackImage[] {
    const tokens: FallbackImageToken[] = [
      { suffix: '0', fallbackTitle: 'Cover' },
    ];

    return tokens
      .map((token) => ({
        src: this.plannerImageForIndex(token.suffix),
        fallbackTitle: token.fallbackTitle
      }));
  }

  get printPdfLink(): string {
    if (!this.currentProductIdentification) {
      return '';
    }

    return `http://printpdf.media.calvendo.net/${this.currentProductIdentification.ean}.pdf`;
  }

  calendarImageForIndex(index: string): string {
    return `/${this.currentProductIdentification!.ean}_${index}.jpg`;
  }

  canvasImageForSuffix(suffix: string): string {
    return `/${this.currentProductIdentification!.ean}${suffix}.jpg`;
  }

  puzzleImageForIndex(suffix: string): string {
    return `/${this.currentProductIdentification!.ean}${suffix}.jpg`;
  }

  plannerImageForIndex(index: string): string {
    return `/${this.currentProductIdentification!.ean}_${index}.jpg`;
  }

  bookImageForIndex(suffix: string): string {
    return `/${this.currentProductIdentification!.ean}_${suffix}.jpg`;
  }

  translatedMedium(medium: Medium): string {
    return mediumMap[medium];
  }

}
